import React, { useState } from 'react';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import {
  renderQueuedImageStatus,
  renderQueuedImageCancelButton
} from '@/components/dashboard/gallery/ImagesGalleryHelpers';
import { truncateToWords, copyToClipboard } from '@/utils/common/textUtils';
import { CopyButton } from './CopyButton';

interface QueuedImageTileProps {
  queuedImage: QueuedVideo;
  handleCancel?: () => void;
  cancelQueuedVideo?: (id: string) => void;
}

/**
 * Shared component for rendering queued/processing/failed image tiles
 * Used in both dashboard gallery and admin content review
 */
const QueuedImageTile: React.FC<QueuedImageTileProps> = ({
  queuedImage,
  handleCancel,
  cancelQueuedVideo
}) => {
  const [copied, setCopied] = useState(false);
  const isActiveState = queuedImage.status === 'queued' || queuedImage.status === 'processing';

  const handleCopyPrompt = () => {
    copyToClipboard(queuedImage.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`rounded-xl overflow-hidden border-2 border-dashed relative ${
        isActiveState
          ? 'border-gray-400 tile-glow'
          : 'bg-gradient-to-br from-blue-50 to-indigo-100 border-blue-300'
      }`}
      style={{
        aspectRatio: '1/1',
        ...(isActiveState
          ? { background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)' }
          : {})
      }}
    >
      <div className="w-full h-full flex flex-col items-center justify-center p-4 relative">
        {renderQueuedImageCancelButton(queuedImage, handleCancel, cancelQueuedVideo)}
        {isActiveState ? (
          <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden">
            {/* Multiple organic shimmer blobs */}
            <div className="absolute shimmer-morph morph-1" aria-hidden="true"></div>
            <div className="absolute shimmer-morph morph-2" aria-hidden="true"></div>
            <div className="absolute shimmer-morph morph-3" aria-hidden="true"></div>
            <div className="absolute shimmer-morph morph-4" aria-hidden="true"></div>
            <div className="absolute shimmer-morph morph-5" aria-hidden="true"></div>
            <div className="absolute shimmer-morph morph-6" aria-hidden="true"></div>

            {/* Content */}
            <div className="relative z-10 text-center">
              {queuedImage.status === 'queued' && (
                <>
                  <div className="text-xl font-semibold text-white mb-2">Queued</div>
                  <div className="text-xs text-white mb-2 flex items-center justify-center gap-1">
                    <span>{truncateToWords(queuedImage.prompt).text}</span>
                    <CopyButton copied={copied} onClick={handleCopyPrompt} />
                  </div>
                </>
              )}
              {queuedImage.status === 'processing' && (
                <>
                  <div className="text-xl font-semibold text-white mb-2">
                    {queuedImage.progress ? `${queuedImage.progress}%` : '0%'}
                  </div>
                  <div className="text-xs text-white mb-2 flex items-center justify-center gap-1">
                    <span>{truncateToWords(queuedImage.prompt).text}</span>
                    <CopyButton copied={copied} onClick={handleCopyPrompt} />
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center">{renderQueuedImageStatus(queuedImage, copied, setCopied)}</div>
        )}
      </div>
    </div>
  );
};

export default QueuedImageTile;
